import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import {
  Archive,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

import {
  useNavigate,
} from "react-router-dom";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DeadStockReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const navigate =
    useNavigate();

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "";

  const branch =
    localStorage.getItem(
      "branchType"
    ) || "";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branch,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branch,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    branchOptions,
    setBranchOptions,
  ] = useState([]);

  const [
    selectedBranch,
    setSelectedBranch,
  ] = useState(
    branchId || ""
  );

  const [
    itemCategoryList,
    setItemCategoryList,
  ] = useState([]);

  const [
    selectedItemCategory,
    setSelectedItemCategory,
  ] = useState("");

  const [
    itemNameList,
    setItemNameList,
  ] = useState([]);

  const [
    selectedItemName,
    setSelectedItemName,
  ] = useState("");

  const [
    itemSubCategoryList,
    setItemSubCategoryList,
  ] = useState([]);

  const [
    selectedItemSubCategory,
    setSelectedItemSubCategory,
  ] = useState("");

  const [
    itemcode,
    setItemCode,
  ] = useState("");

  /* =========================================================
     DATA STATE
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  const [
    totalAmount,
    setTotalAmount,
  ] = useState(0);

  /* =========================================================
     PAGINATION
  ========================================================= */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const [
    rowsPerPage,
    setRowsPerPage,
  ] = useState(10);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD BRANCH + CATEGORY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/fillDDL_dsRpt",
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        if (
          res.data
            ?.success
        ) {
          setBranchOptions(
            res.data
              .branch ||
              []
          );

          setItemCategoryList(
            res.data
              .itemcategory ||
              []
          );
        }
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [commonHeaders]);

  /* =========================================================
     LOAD ITEM NAMES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    if (
      selectedBranch &&
      fromDate &&
      toDate
    ) {
      setLoading(true);

      axios
        .get(
          "https://nissi-india.com/api/fillItemName_dsRpt",
          {
            params: {
              branchid:
                selectedBranch,

              fromdate:
                fromDate,

              todate:
                toDate,
            },

            headers:
              commonHeaders,
          }
        )
        .then((res) => {
          setItemNameList(
            res.data
              ?.itemname ||
              []
          );
        })
        .catch((err) => {
          console.error(
            "Item name fetch failed",
            err
          );

          setItemNameList(
            []
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setItemNameList(
        []
      );
    }
  }, [
    selectedBranch,
    fromDate,
    toDate,
    commonHeaders,
  ]);

  /* =========================================================
     LOAD SUB CATEGORY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    if (
      selectedItemCategory
    ) {
      axios
        .get(
          "https://nissi-india.com/api/getItemSubcategory_dsRpt",
          {
            params: {
              itemcategoryid:
                selectedItemCategory,
            },

            headers:
              commonHeaders,
          }
        )
        .then((res) => {
          setItemSubCategoryList(
            res.data
              ?.subcategories ||
              []
          );
        })
        .catch((err) => {
          console.error(
            "Subcategory fetch failed",
            err
          );

          setItemSubCategoryList(
            []
          );
        });
    } else {
      setItemSubCategoryList(
        []
      );

      setSelectedItemSubCategory(
        ""
      );
    }
  }, [
    selectedItemCategory,
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    () => {
      if (
        !selectedBranch ||
        !fromDate ||
        !toDate
      ) {
        toast.error(
          "Please select branch and date range"
        );

        return;
      }

      setLoading(
        true
      );

      const params = {
        branchid:
          selectedBranch,

        itemcode:
          itemcode || "",

        itemname:
          selectedItemName ||
          "",

        fromdate:
          fromDate,

        todate:
          toDate,

        itemcategoryid:
          selectedItemCategory ||
          "",

        itemsubcategoryid:
          selectedItemSubCategory ||
          "",
      };

      axios
        .get(
          "https://nissi-india.com/api/deadStockReport_list",
          {
            params,
            headers:
              commonHeaders,
          }
        )
        .then((res) => {
          const rows =
            res.data
              ?.data ||
            [];

          setData(
            rows
          );

          setTotalAmount(
            res.data
              ?.totalAmount ||
              0
          );

          setCurrentPage(
            1
          );

          toast.success(
            `Loaded ${rows.length} records`
          );
        })
        .catch((err) => {
          console.error(
            "Dead stock report fetch failed",
            err
          );

          toast.error(
            "Failed to fetch dead stock report"
          );

          setData([]);

          setTotalAmount(
            0
          );
        })
        .finally(() => {
          setLoading(false);
        });
    };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint =
    () => {
      const params = {
        branchid:
          selectedBranch,

        itemcode:
          itemcode || "",

        itemname:
          selectedItemName ||
          "",

        fromdate:
          fromDate,

        todate:
          toDate,

        itemcategoryid:
          selectedItemCategory ||
          "",

        itemsubcategoryid:
          selectedItemSubCategory ||
          "",
      };

      navigate(
        "/inventory/DeadStockReportPrint",
        {
          state: {
            params,
            headers:
              commonHeaders,
          },
        }
      );
    };

  /* =========================================================
     EXCEL EXPORT
     REAL EXISTING FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleDownloadExcel =
    () => {
      if (
        data.length ===
        0
      ) {
        toast.warning(
          "No data to export"
        );

        return;
      }

      const exportData =
        data.map(
          (
            row,
            index
          ) => ({
            "S.No":
              index + 1,

            "Branch Name":
              row.fld_BranchName ||
              "",

            "Item Code":
              row.fld_itemcode ||
              "",

            "Item Name":
              row.fld_itemname ||
              "",

            UOM:
              row.fld_uomname ||
              "",

            Category:
              row.fld_itemcategory_name ||
              "",

            "Sub Category":
              row.fld_itemsubCategoryName ||
              "",

            Qty:
              row.fld_qty ||
              0,

            Price:
              row.fld_price ||
              0,

            Amount:
              row.amount ||
              0,

            "Transaction Date":
              row.fld_transdate ||
              "",
          })
        );

      const ws =
        XLSX.utils.json_to_sheet(
          exportData
        );

      const wb =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        wb,
        ws,
        "Dead Stock Report"
      );

      const excelBuffer =
        XLSX.write(
          wb,
          {
            bookType:
              "xlsx",

            type:
              "array",
          }
        );

      const blob =
        new Blob(
          [excelBuffer],
          {
            type: "application/octet-stream",
          }
        );

      saveAs(
        blob,
        "DeadStockReport.xlsx"
      );

      toast.success(
        "Excel file downloaded successfully"
      );
    };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setFromDate("");

      setToDate("");

      setSelectedBranch(
        branchId || ""
      );

      setSelectedItemCategory(
        ""
      );

      setSelectedItemName(
        ""
      );

      setSelectedItemSubCategory(
        ""
      );

      setItemCode("");

      setItemNameList([]);

      setItemSubCategoryList(
        []
      );

      setData([]);

      setTotalAmount(
        0
      );

      setCurrentPage(
        1
      );
    };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency =
    (amount) => {
      return new Intl.NumberFormat(
        "en-IN",
        {
          style:
            "currency",

          currency:
            "INR",

          minimumFractionDigits:
            2,

          maximumFractionDigits:
            2,
        }
      ).format(
        Number(
          amount || 0
        )
      );
    };

  /* =========================================================
     PAGINATION
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const totalPages =
    Math.ceil(
      data.length /
        rowsPerPage
    );

  const paginatedData =
    useMemo(() => {
      const startIndex =
        (currentPage -
          1) *
        rowsPerPage;

      return data.slice(
        startIndex,
        startIndex +
          rowsPerPage
      );
    }, [
      data,
      currentPage,
      rowsPerPage,
    ]);

  const startRecord =
    data.length === 0
      ? 0
      : (currentPage -
          1) *
          rowsPerPage +
        1;

  const endRecord =
    Math.min(
      currentPage *
        rowsPerPage,
      data.length
    );

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Dead Stock Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Archive className="w-5 h-5 text-white" />
              </div>

              <div>
                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Dead Stock Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Analyze and manage non-moving inventory items
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
                SAME POSITION AS FIRST PAGE
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleDownloadExcel
                }
                disabled={
                  data.length ===
                  0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <select
                    value={
                      selectedBranch
                    }
                    onChange={(e) =>
                      setSelectedBranch(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {branchOptions.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Category
                  </label>

                  <select
                    value={
                      selectedItemCategory
                    }
                    onChange={(e) =>
                      setSelectedItemCategory(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Category
                    </option>

                    {itemCategoryList.map(
                      (c) => (
                        <option
                          key={
                            c.fld_itemcategoryid
                          }
                          value={
                            c.fld_itemcategoryid
                          }
                        >
                          {
                            c.fld_itemcategory_name
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUB CATEGORY */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Sub Category
                  </label>

                  <select
                    value={
                      selectedItemSubCategory
                    }
                    onChange={(e) =>
                      setSelectedItemSubCategory(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Sub Category
                    </option>

                    {itemSubCategoryList.map(
                      (s) => (
                        <option
                          key={
                            s.fld_itemsubCategoryid
                          }
                          value={
                            s.fld_itemsubCategoryid
                          }
                        >
                          {
                            s.fld_itemsubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* ITEM NAME */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Name
                  </label>

                  <select
                    value={
                      selectedItemName
                    }
                    onChange={(e) =>
                      setSelectedItemName(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Item Name
                    </option>

                    {itemNameList.map(
                      (n) => (
                        <option
                          key={
                            n.fld_itemid
                          }
                          value={
                            n.fld_itemid
                          }
                        >
                          {
                            n.fld_itemname
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* ITEM CODE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Code
                  </label>

                  <input
                    type="text"
                    value={
                      itemcode
                    }
                    onChange={(e) =>
                      setItemCode(
                        e.target.value
                      )
                    }
                    placeholder="Enter item code"
                    className={
                      inputClass
                    }
                    onKeyDown={(
                      e
                    ) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

              {/* LEFT */}

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Dead Stock Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {/* RIGHT */}

              {data.length >
                0 && (
                <div className="flex flex-wrap items-center gap-4">

                  {/* TOTAL */}

                  <div
                    className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                      isDark
                        ? "bg-green-900/30 text-green-300"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    Total Amount:{" "}
                    {formatCurrency(
                      totalAmount
                    )}
                  </div>

                  {/* ROW SIZE */}

                  <div className="flex items-center gap-2">

                    <label
                      className={`text-sm ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-500"
                      }`}
                    >
                      Rows:
                    </label>

                    <select
                      value={
                        rowsPerPage
                      }
                      onChange={(e) => {
                        setRowsPerPage(
                          Number(
                            e.target.value
                          )
                        );

                        setCurrentPage(
                          1
                        );
                      }}
                      className={`px-3 py-2 border rounded-lg text-sm ${
                        isDark
                          ? "bg-slate-800 border-slate-600 text-white"
                          : "bg-white border-slate-300 text-slate-800"
                      }`}
                    >
                      <option value={10}>
                        10
                      </option>

                      <option value={25}>
                        25
                      </option>

                      <option value={50}>
                        50
                      </option>

                      <option value={100}>
                        100
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading dead stock records...
              </p>
            </div>
          ) : data.length >
            0 ? (
            <>
              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  {/* HEADER */}

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Branch
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Item Code
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Item Name
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        UOM
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Category
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Sub Category
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Qty
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Price
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Amount
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Trans Date
                      </th>
                    </tr>
                  </thead>

                  {/* BODY */}

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >
                    {paginatedData.map(
                      (
                        row,
                        index
                      ) => (
                        <tr
                          key={
                            index
                          }
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          {/* BRANCH */}

                          <td className="px-4 py-3 font-medium">
                            {row.fld_BranchName ||
                              "-"}
                          </td>

                          {/* ITEM CODE */}

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {row.fld_itemcode ||
                                "-"}
                            </span>
                          </td>

                          {/* ITEM NAME */}

                          <td className="px-4 py-3">
                            {row.fld_itemname ||
                              "-"}
                          </td>

                          {/* UOM */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {row.fld_uomname ||
                              "-"}
                          </td>

                          {/* CATEGORY */}

                          <td className="px-4 py-3">
                            {row.fld_itemcategory_name ||
                              "-"}
                          </td>

                          {/* SUB CATEGORY */}

                          <td className="px-4 py-3">
                            {row.fld_itemsubCategoryName ||
                              "-"}
                          </td>

                          {/* QTY */}

                          <td className="px-4 py-3 whitespace-nowrap font-medium">
                            {row.fld_qty ??
                              "-"}
                          </td>

                          {/* PRICE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {formatCurrency(
                              row.fld_price ||
                                0
                            )}
                          </td>

                          {/* AMOUNT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap font-semibold ${
                              isDark
                                ? "text-green-300"
                                : "text-green-700"
                            }`}
                          >
                            {formatCurrency(
                              row.amount ||
                                0
                            )}
                          </td>

                          {/* TRANS DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {row.fld_transdate ||
                              "-"}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>

                  {/* =========================================
                      TOTAL FOOTER
                  ========================================= */}

                  <tfoot
                    className={
                      isDark
                        ? "bg-slate-900/70"
                        : "bg-slate-100"
                    }
                  >
                    <tr>

                      <td
                        colSpan="8"
                        className={`px-4 py-3 text-right text-sm font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Total Amount
                      </td>

                      <td
                        className={`px-4 py-3 whitespace-nowrap text-sm font-bold ${
                          isDark
                            ? "text-green-300"
                            : "text-green-700"
                        }`}
                      >
                        {formatCurrency(
                          totalAmount
                        )}
                      </td>

                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* =============================================
                  PAGINATION
              ============================================= */}

              <div
                className={`p-6 border-t ${
                  isDark
                    ? "border-slate-700"
                    : "border-slate-200"
                }`}
              >
                <div
                  className="
                    flex
                    flex-col
                    md:flex-row
                    md:items-center
                    md:justify-between
                    gap-4
                  "
                >

                  {/* RECORD INFO */}

                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Showing{" "}
                    <span className="font-semibold">
                      {startRecord}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold">
                      {endRecord}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold">
                      {data.length}
                    </span>{" "}
                    entries
                  </div>

                  {/* PAGINATION BUTTONS */}

                  <div className="flex items-center gap-2">

                    {/* PREV */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (prev) =>
                            Math.max(
                              prev -
                                1,
                              1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                        1
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage ===
                        1
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <FaChevronLeft
                        size={12}
                      />

                      Prev
                    </button>

                    {/* PAGE NUMBER */}

                    <span
                      className={`px-4 py-2 text-sm font-semibold rounded-lg bg-${baseColor}-${baseShade} text-white`}
                    >
                      Page{" "}
                      {currentPage}{" "}
                      of{" "}
                      {totalPages ||
                        1}
                    </span>

                    {/* NEXT */}

                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          (prev) =>
                            Math.min(
                              prev +
                                1,
                              totalPages ||
                                1
                            )
                        )
                      }
                      disabled={
                        currentPage ===
                          totalPages ||
                        totalPages ===
                          0
                      }
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        currentPage ===
                          totalPages ||
                        totalPages ===
                          0
                          ? isDark
                            ? "border-slate-700 text-slate-600 cursor-not-allowed"
                            : "border-slate-200 text-slate-400 cursor-not-allowed"
                          : isDark
                          ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                          : "border-slate-300 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      Next

                      <FaChevronRight
                        size={12}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaEye
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No dead stock records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default DeadStockReport;